import { useState } from 'react'
import { motion } from 'framer-motion'
import { unsplash, imageIds } from '@/lib/images'
import { Play, X } from 'lucide-react'
import { useSettings } from '@/hooks/useSettings'

export function HeroSection() {
  const { storefront, hero } = useSettings()
  const [showVideo, setShowVideo] = useState(false)

  const headline = storefront?.heroHeadline ?? 'little sky'
  const subheadline = storefront?.heroSubheadline ?? 'Perfumes for details'
  const isDefaultHeadline = headline.trim().toLowerCase() === 'little sky'

  const imageUrl = hero?.imageUrl || unsplash(imageIds.hero, 1920, 1080)
  const videoUrl = hero?.videoUrl
  const showFilmButton = hero?.showFilmButton !== false
  const filmButtonText = hero?.filmButtonText ?? 'Watch film'
  const isVideoBackground = Boolean(videoUrl)

  return (
    <section className="relative flex min-h-screen items-end overflow-hidden bg-wd-deep pb-12 text-white">
      <div className="absolute inset-0">
        {isVideoBackground ? (
          <video
            autoPlay
            muted
            loop
            playsInline
            poster={hero?.videoPosterUrl || imageUrl}
            className="h-full w-full object-cover opacity-90"
          >
            <source src={videoUrl} />
          </video>
        ) : (
          <img
            src={imageUrl}
            alt="Fragrance still life"
            className="h-full w-full object-cover opacity-90"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-wd-deep via-wd-deep/40 to-transparent" />
      </div>

      <div className="grid-lines text-white">
        <div />
        <div />
        <div />
        <div />
        <div />
      </div>

      <div className="relative z-10 w-full px-6 lg:px-10">
        {/* Wordmark → tagline → film button, stacked as one centred vertical unit.
            They reflow without shifting because the column is flex-centred on a
            fixed max width; the tagline is the muted sans read under the serif. */}
        <div className="mx-auto flex max-w-text-block flex-col items-center gap-4 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="font-display text-[clamp(4rem,16vw,12.5rem)] font-normal normal-case leading-[0.9] tracking-tight"
          >
            {isDefaultHeadline ? (
              // Same wordmark as the nav. Bigilla's ascender line is 0.769em, so
              // rendering the image at that height keeps letters exactly as tall
              // as the type it replaces.
              <img
                src="/Little-sky-full-type-crop.png"
                alt={headline}
                className="block h-[0.769em] w-auto object-contain"
              />
            ) : (
              headline.toLowerCase()
            )}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="font-sans text-sm leading-relaxed tracking-[0.08em] text-white/70 md:text-base"
          >
            {subheadline}
          </motion.p>

          {showFilmButton && (
            <button
              onClick={() => setShowVideo(true)}
              className="group mt-2 flex items-center gap-3 text-xs normal-case tracking-[0.12em]"
            >
              <span className="border-b border-white/40 pb-0.5 transition-colors group-hover:border-white">
                {filmButtonText}
              </span>
              <span className="flex h-10 w-10 items-center justify-center rounded-full border border-white/40 transition-colors group-hover:bg-white group-hover:text-wd-orange">
                <Play className="h-3 w-3 fill-current" />
              </span>
            </button>
          )}
        </div>
      </div>

      {showVideo && videoUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-deep/90 p-6">
          <button
            onClick={() => setShowVideo(false)}
            className="absolute right-6 top-6 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
            aria-label="Close video"
          >
            <X className="h-5 w-5" />
          </button>
          <video autoPlay controls className="max-h-[80vh] max-w-full rounded-lg shadow-2xl">
            <source src={videoUrl} />
          </video>
        </div>
      )}
    </section>
  )
}
